import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getSortedSummary } from 'utils/getSortedSummary';
import { calculateTextDiff, formatTextForDisplay } from 'utils/textDiff';

import { NamespacesList } from 'core/enums/common';

import { IFormSummaryStory } from 'components/TicketSteps/GeneralStep/EditGeneralStep';

import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

export const UserStoriesChangesDisplay = ({
	oldValue,
	newValue,
}: {
	oldValue: Record<string, any>;
	newValue: Record<string, any>;
}) => {
	const { t: generalStepTranslate } = useTranslation(NamespacesList['generalStep']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const getUpdatedValueStyles = ({
		oldValue,
		newValue,
		isDeleted,
		showStyles,
	}: {
		oldValue: string;
		newValue: string;
		isDeleted: boolean;
		showStyles: boolean;
	}) => {
		if (showStyles) {
			if (isDeleted) {
				return { backgroundColor: '#FB0B244C' };
			}

			if (oldValue.trim() !== newValue.trim()) {
				return { backgroundColor: '#01C8604C' };
			}
		}

		return {};
	};

	const renderTextWithDiff = (
		oldText: string,
		newText: string,
		displayText: string,
		isDeleted: boolean,
		isNewBlock: boolean
	) => {
		// Si supprimé, afficher avec la couleur rouge dans les deux blocs
		if (isDeleted) {
			return <span style={{ backgroundColor: '#FB0B244C' }}>{displayText}</span>;
		}

		if (!isNewBlock || !oldText || !newText || oldText.trim() === newText.trim()) {
			return displayText;
		}

		const diffParts = calculateTextDiff(oldText || '', newText || '');
		return formatTextForDisplay(diffParts, isNewBlock, isDeleted);
	};

	const getUserStoriesBlock = ({
		newValue,
		oldValue,
		isNewBlock,
	}: {
		oldValue: Record<string, any>;
		newValue: Record<string, any>;
		isNewBlock: boolean;
	}) => {
		const usedValue = isNewBlock ? newValue : oldValue;

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					padding: '12px 16px',
					borderRadius: '8px',
					overflow: 'auto',
					backgroundColor: '#F3F4F580',
				}}
			>
				{usedValue?.description ? (
					<Box>
						<Typography sx={{ fontWeight: 700, margin: '0 10px 5px 0', color: titleTextColor }}>
							{generalStepTranslate('commonText.description')}
						</Typography>

						<Typography
							sx={{
								wordWrap: 'break-word',
								whiteSpace: 'pre-line',
								paddingRight: '10px',
								color: regularTextColor,
							}}
						>
							{renderTextWithDiff(
								oldValue.description || '',
								newValue.description || '',
								usedValue?.description || '',
								usedValue.isDeleted,
								isNewBlock
							)}
						</Typography>
					</Box>
				) : null}
				{usedValue?.ticketSummaries?.length ? (
					<Box>
						<Typography
							sx={{
								fontWeight: 700,
								margin: '8px 10px 5px 0',
								color: titleTextColor,
							}}
						>
							{commonTranslate('modelsInput.userStories')}:
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '8px',
								wordWrap: 'break-word',
								whiteSpace: 'pre-line',
								paddingRight: '10px',
							}}
						>
							{getSortedSummary(usedValue.ticketSummaries).map(
								(story: IFormSummaryStory, index: number) => (
									<Box key={story.data}>
										<Typography
											sx={{
												fontWeight: 700,
												margin: '8px 10px 5px 0',
												color: titleTextColor,
											}}
										>
											{commonTranslate('modelsInput.story')}:
										</Typography>

										<Typography
											key={index}
											sx={{
												wordWrap: 'break-word',
												whiteSpace: 'pre-line',
												color: regularTextColor,
											}}
										>
											{`${index + 1}.`}
											{renderTextWithDiff(
												oldValue.ticketSummaries[index]?.data || '',
												newValue.ticketSummaries[index]?.data || '',
												story.data || '',
												story.isDeleted,
												isNewBlock
											)}
										</Typography>
										<Box>
											<Typography
												sx={{
													fontWeight: 700,
													margin: '8px 10px 5px 0',
													color: titleTextColor,
												}}
											>
												{generalStepTranslate('commonText.scenarios')}
											</Typography>
											{story?.summaryScenarios?.map((scenario, scenarioIndex) => (
												<>
													<Typography
														key={index}
														sx={{
															wordWrap: 'break-word',
															whiteSpace: 'pre-line',
															color: regularTextColor,
														}}
													>
														{`${index + 1}.${scenarioIndex + 1}. `}
														{renderTextWithDiff(
															oldValue.ticketSummaries[index]?.summaryScenarios[scenarioIndex]?.title || '',
															newValue.ticketSummaries[index]?.summaryScenarios[scenarioIndex]?.title || '',
															scenario.title || '',
															scenario.isDeleted,
															isNewBlock
														)}
													</Typography>

													<Typography
														key={index}
														sx={{
															wordWrap: 'break-word',
															whiteSpace: 'pre-line',
															color: regularTextColor,
														}}
													>
														{renderTextWithDiff(
															oldValue.ticketSummaries[index]?.summaryScenarios[scenarioIndex]?.description || '',
															newValue.ticketSummaries[index]?.summaryScenarios[scenarioIndex]?.description || '',
															scenario.description || '',
															scenario.isDeleted,
															isNewBlock
														)}
													</Typography>
												</>
											)) || null}
										</Box>

										<Box>
											<Typography
												sx={{
													fontWeight: 700,
													margin: '8px 10px 5px 0',
													color: titleTextColor,
												}}
											>
												{generalStepTranslate('commonText.acceptanceCriteria')}
											</Typography>
											{story?.summaryAcceptanceCriteria?.map((criteria, criteriaIndex) => (
												<>
													<Typography
														key={index}
														sx={{
															wordWrap: 'break-word',
															whiteSpace: 'pre-line',
															color: regularTextColor,
														}}
													>
														{`${index + 1}.${criteriaIndex + 1}. `}
														{renderTextWithDiff(
															oldValue.ticketSummaries[index]?.summaryAcceptanceCriteria[criteriaIndex]?.title || '',
															newValue.ticketSummaries[index]?.summaryAcceptanceCriteria[criteriaIndex]?.title || '',
															criteria.title || '',
															criteria.isDeleted,
															isNewBlock
														)}
													</Typography>

													<Typography
														key={index}
														sx={{
															wordWrap: 'break-word',
															whiteSpace: 'pre-line',
															color: regularTextColor,
														}}
													>
														{renderTextWithDiff(
															oldValue.ticketSummaries[index]?.summaryAcceptanceCriteria[criteriaIndex]?.description || '',
															newValue.ticketSummaries[index]?.summaryAcceptanceCriteria[criteriaIndex]?.description || '',
															criteria.description || '',
															criteria.isDeleted,
															isNewBlock
														)}
													</Typography>
												</>
											)) || null}
										</Box>
									</Box>
								)
							)}
						</Box>
					</Box>
				) : null}
			</Box>
		);
	};

	return (
		<Box sx={{ display: 'flex', maxHeight: '90%', gap: '24px' }}>
			{getUserStoriesBlock({ newValue, oldValue, isNewBlock: false })}
			{getUserStoriesBlock({ newValue, oldValue, isNewBlock: true })}
		</Box>
	);
};
